// src/pages/Templates.jsx

import React, { useState, useEffect } from 'react';
import { useWabaAccounts, withAccount, accountLabel } from '../lib/waba';
import {
  FiInfo,
  FiRefreshCw,
  FiPlus,
  FiAlertTriangle,
  FiAlertCircle,
  FiSearch,
  FiX,
  FiFileText,
  FiLoader,
  FiSave,
  FiEye,
  FiTrash,
  FiExternalLink,
  FiPhone,
  FiCopy,
  FiSmartphone,
  FiShoppingBag,
  FiGrid,
  FiCornerUpLeft
} from 'react-icons/fi';

/** AccordionSection – reusable collapsible panel */
function AccordionSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-600 rounded-lg overflow-hidden mb-4">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex justify-between items-center px-4 py-2 bg-gray-700 text-gray-100 hover:bg-gray-600"
      >
        <span className="font-medium">{title}</span>
        <svg
          className={`w-5 h-5 transform transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="p-4 bg-gray-800">{children}</div>}
    </div>
  );
}

// Meta's three template categories. Single source for the create form's
// selector and the list filter, so the two cannot drift apart.
const WA_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];

// Meta writes the copy of AUTHENTICATION templates itself — the form only
// chooses the settings, and these are the strings Meta renders for en_US.
const AUTH_BODY_TEXT = '{{1}} is your verification code.';
const AUTH_SECURITY_TEXT = ' For your security, do not share this code.';
const AUTH_DEFAULT_EXPIRY_MINUTES = '10';

function authBodyText(addSecurityRecommendation) {
  return AUTH_BODY_TEXT + (addSecurityRecommendation ? AUTH_SECURITY_TEXT : '');
}

function authFooterText(minutes) {
  const n = parseInt(minutes, 10);
  if (!Number.isFinite(n) || n < 1) return '';
  return `This code expires in ${n} minute${n === 1 ? '' : 's'}.`;
}

const MAX_TEMPLATE_BUTTONS = 3;
const TEMPLATE_BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Quick Reply' },
  { value: 'URL', label: 'URL' },
  { value: 'PHONE_NUMBER', label: 'Phone Number' },
  { value: 'OTP_COPY_CODE', label: 'OTP Copy Code' },
  { value: 'OTP_ONE_TAP', label: 'OTP One Tap' },
  { value: 'CATALOG', label: 'Catalog' },
  { value: 'MPM', label: 'Multi-Product (MPM)' }
];

function newTemplateButton() {
  return {
    kind: 'QUICK_REPLY',
    text: '',
    url: '',
    urlExample: '',
    phone_number: '',
    autofill_text: '',
    package_name: '',
    signature_hash: ''
  };
}

function normalizeTemplateButton(button) {
  const type = String(button?.type || '').toUpperCase();
  if (type === 'OTP') {
    const otpType = String(button?.otp_type || '').toUpperCase();
    return {
      kind: otpType === 'ONE_TAP' ? 'OTP_ONE_TAP' : 'OTP_COPY_CODE',
      text: button?.text || '',
      url: '',
      urlExample: '',
      phone_number: '',
      autofill_text: button?.autofill_text || '',
      package_name: button?.package_name || '',
      signature_hash: button?.signature_hash || ''
    };
  }

  return {
    kind: type || 'QUICK_REPLY',
    text: button?.text || '',
    url: button?.url || '',
    urlExample: Array.isArray(button?.example) ? (button.example[0] || '') : '',
    phone_number: button?.phone_number || '',
    autofill_text: '',
    package_name: '',
    signature_hash: ''
  };
}

function buildTemplateButtonPayload(button) {
  const kind = String(button?.kind || '').toUpperCase();
  const text = String(button?.text || '').trim();

  if (kind === 'QUICK_REPLY') return { type: 'QUICK_REPLY', text };
  if (kind === 'URL') {
    const out = { type: 'URL', text, url: String(button?.url || '').trim() };
    const example = String(button?.urlExample || '').trim();
    if (example) out.example = [example];
    return out;
  }
  if (kind === 'PHONE_NUMBER') {
    return { type: 'PHONE_NUMBER', text, phone_number: String(button?.phone_number || '').trim() };
  }
  if (kind === 'OTP_COPY_CODE') return { type: 'OTP', otp_type: 'COPY_CODE', text };
  if (kind === 'OTP_ONE_TAP') {
    return {
      type: 'OTP',
      otp_type: 'ONE_TAP',
      text,
      autofill_text: String(button?.autofill_text || '').trim(),
      package_name: String(button?.package_name || '').trim(),
      signature_hash: String(button?.signature_hash || '').trim()
    };
  }
  if (kind === 'CATALOG') return { type: 'CATALOG', text };
  if (kind === 'MPM') return { type: 'MPM', text };

  return { type: 'QUICK_REPLY', text };
}

function validateTemplateButtons(buttons, waCategory) {
  if (!Array.isArray(buttons)) return '';
  if (buttons.length > MAX_TEMPLATE_BUTTONS) {
    return `You can add up to ${MAX_TEMPLATE_BUTTONS} buttons.`;
  }

  const kinds = buttons.map(b => String(b?.kind || '').toUpperCase());
  const hasOtp = kinds.includes('OTP_COPY_CODE') || kinds.includes('OTP_ONE_TAP');
  const hasCatalog = kinds.includes('CATALOG');
  const hasMpm = kinds.includes('MPM');

  if ((hasOtp || hasCatalog || hasMpm) && buttons.length > 1) {
    return 'OTP, Catalog, and MPM buttons must be used alone (single button template).';
  }
  const isAuth = String(waCategory || '').toUpperCase() === 'AUTHENTICATION';
  if (hasOtp && !isAuth) {
    return 'OTP buttons are only valid for AUTHENTICATION templates.';
  }
  if (isAuth && !hasOtp) {
    return 'AUTHENTICATION templates need one OTP button (Copy Code or One Tap).';
  }

  // Meta's per-type limits. Exceeding them is a rejection at submit time with a
  // message that does not name the button, so catch it here.
  if (kinds.filter(k => k === 'URL').length > 2) return 'At most 2 URL buttons are allowed.';
  if (kinds.filter(k => k === 'PHONE_NUMBER').length > 1) return 'Only 1 phone number button is allowed.';

  for (let i = 0; i < buttons.length; i += 1) {
    const b = buttons[i];
    const idx = i + 1;
    if (!String(b?.text || '').trim()) return `Button ${idx}: text is required.`;
    if (String(b?.text || '').trim().length > 25) {
      return `Button ${idx}: text must be 25 characters or fewer.`;
    }

    const kind = String(b?.kind || '').toUpperCase();
    if (kind === 'URL') {
      const url = String(b?.url || '').trim();
      if (!url) return `Button ${idx}: URL is required.`;
      if (!/^https?:\/\//i.test(url)) return `Button ${idx}: URL must start with http:// or https://`;
      // A URL carrying a variable needs a sample, or Meta cannot review where
      // the link actually goes and rejects the whole template.
      if (/\{\{\s*\d+\s*\}\}/.test(url) && !String(b?.urlExample || '').trim()) {
        return `Button ${idx}: a URL with {{1}} needs a URL example so Meta can review it.`;
      }
    }
    if (kind === 'PHONE_NUMBER') {
      const phone = String(b?.phone_number || '').trim();
      if (!phone) return `Button ${idx}: phone number is required.`;
      if (!/^\+?\d{7,15}$/.test(phone)) {
        return `Button ${idx}: phone number must be digits with the country code, e.g. 919480012345.`;
      }
    }
    if (kind === 'OTP_ONE_TAP') {
      if (!String(b?.autofill_text || '').trim()) return `Button ${idx}: autofill text is required for OTP one-tap.`;
      if (!String(b?.package_name || '').trim()) return `Button ${idx}: package name is required for OTP one-tap.`;
      if (!String(b?.signature_hash || '').trim()) return `Button ${idx}: signature hash is required for OTP one-tap.`;
    }
  }

  return '';
}

function formatFieldLabel(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

function DetailValue({ value }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">-</span>;
  }

  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }

  if (typeof value === 'number') {
    return <span>{String(value)}</span>;
  }

  if (isHttpUrl(value)) {
    return (
      <a href={value} target="_blank" rel="noopener noreferrer" className="text-brand-700 break-all hover:underline">
        {value}
      </a>
    );
  }

  if (typeof value === 'string') {
    return <span className="break-words">{value}</span>;
  }

  return (
    <pre className="bg-gray-50 border border-gray-200 rounded-md p-2 text-xs overflow-auto whitespace-pre-wrap">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   WhatsApp preview

   Renders the template the way the recipient's phone will, which is the only
   way to catch the things Meta's approval will not: a placeholder left in the
   middle of a sentence, *bold* markers that never close, a button label too
   long for its row.
   ══════════════════════════════════════════════════════════════════════════ */

// WhatsApp's own inline formatting. Applied longest-delimiter-first so ```mono```
// is not eaten by the single-backtick-free rules around it.
const WA_MARKUP = [
  { re: /```([\s\S]+?)```/, render: (t, k) => <code key={k} className="rounded bg-black/5 px-1 font-mono text-[0.9em]">{t}</code> },
  { re: /\*([^*\n]+)\*/,    render: (t, k) => <strong key={k}>{t}</strong> },
  { re: /_([^_\n]+)_/,      render: (t, k) => <em key={k}>{t}</em> },
  { re: /~([^~\n]+)~/,      render: (t, k) => <del key={k}>{t}</del> },
];

function renderWaSpans(text, keyPrefix = 'w') {
  if (!text) return null;

  // Earliest match wins, so the rules do not fight over overlapping ranges.
  let best = null;
  WA_MARKUP.forEach(rule => {
    const m = rule.re.exec(text);
    if (m && (best === null || m.index < best.m.index)) best = { m, rule };
  });

  const placeholder = /\{\{\s*\d+\s*\}\}/.exec(text);
  if (placeholder && (best === null || placeholder.index < best.m.index)) {
    return [
      renderWaSpans(text.slice(0, placeholder.index), `${keyPrefix}a`),
      <span key={`${keyPrefix}p`} className="rounded bg-amber-100 px-1 text-amber-900" title="Unfilled variable">
        {placeholder[0]}
      </span>,
      renderWaSpans(text.slice(placeholder.index + placeholder[0].length), `${keyPrefix}b`),
    ];
  }

  if (!best) return text;
  const { m, rule } = best;
  return [
    renderWaSpans(text.slice(0, m.index), `${keyPrefix}a`),
    rule.render(m[1], `${keyPrefix}m`),
    renderWaSpans(text.slice(m.index + m[0].length), `${keyPrefix}b`),
  ];
}

function WaText({ text, values }) {
  // Sample values stand in for {{n}}; anything without one stays visible as a
  // highlighted placeholder rather than silently reading as finished copy.
  const filled = String(text || '').replace(/\{\{\s*(\d+)\s*\}\}/g,
    (whole, n) => (values && String(values[n] ?? '').trim()) || whole);
  return (
    <>
      {filled.split('\n').map((line, i, all) => (
        <React.Fragment key={`ln-${i}`}>
          {renderWaSpans(line, `l${i}`)}
          {i < all.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
}

const BUTTON_ICON = {
  QUICK_REPLY: FiCornerUpLeft,
  URL: FiExternalLink,
  PHONE_NUMBER: FiPhone,
  OTP_COPY_CODE: FiCopy,
  OTP_ONE_TAP: FiSmartphone,
  CATALOG: FiShoppingBag,
  MPM: FiGrid,
};

/**
 * Graph template -> preview props.
 *
 * Approved templates carry their own example values, so an existing template
 * previews with the text Meta reviewed rather than with raw {{1}} markers.
 */
function previewPropsFromTemplate(tpl) {
  const up = v => String(v || '').toUpperCase();
  const comps = Array.isArray(tpl?.components) ? tpl.components : [];
  const header = comps.find(c => up(c.type) === 'HEADER');
  const body = comps.find(c => up(c.type) === 'BODY');
  const footer = comps.find(c => up(c.type) === 'FOOTER');
  const btnComp = comps.find(c => up(c.type) === 'BUTTONS');

  const format = header ? up(header.format || 'TEXT') : 'NONE';
  let headerType = 'none';
  let headerImage = '';
  if (format === 'TEXT') {
    headerType = 'text';
  } else if (format !== 'NONE') {
    headerType = format.toLowerCase();
    // example.header_handle holds an opaque upload handle, not a link, so it
    // only renders when the template was authored with a real URL.
    const candidate = header?.image?.link || header?.example?.header_url?.[0] || '';
    headerImage = /^https?:\/\//i.test(candidate) ? candidate : '';
  }

  const placeholderValues = {};
  const sample = body?.example?.body_text?.[0];
  if (Array.isArray(sample)) sample.forEach((v, i) => { placeholderValues[String(i + 1)] = v; });

  // Authentication templates carry settings rather than copy, so fall back to
  // the text Meta renders from those settings.
  const isAuth = up(tpl?.category) === 'AUTHENTICATION';
  let bodyText = body?.text || '';
  let footerText = footer?.text || '';
  if (isAuth) {
    if (!bodyText) bodyText = authBodyText(!!body?.add_security_recommendation);
    if (!footerText) footerText = authFooterText(footer?.code_expiration_minutes);
    if (!placeholderValues['1']) placeholderValues['1'] = '123456';
  }

  return {
    headerType,
    headerText: header?.text || '',
    headerImage,
    bodyText,
    footerText,
    buttons: Array.isArray(btnComp?.buttons) ? btnComp.buttons.map(normalizeTemplateButton) : [],
    placeholderValues,
  };
}

function WhatsAppPreview({ headerType, headerText, headerImage, bodyText, footerText, buttons, placeholderValues }) {
  const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const list = Array.isArray(buttons) ? buttons : [];
  const mediaHeader = headerType && !['none', 'text'].includes(headerType);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-300 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="text-lg font-bold text-gray-900">Your template</div>
      </div>

      {/* Chat wallpaper, drawn in CSS so the preview needs no external asset */}
      <div
        className="p-4"
        style={{
          backgroundColor: '#e6ddd4',
          backgroundImage:
            'radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px),' +
            'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '28px 28px, 28px 28px',
          backgroundPosition: '0 0, 14px 14px',
        }}
      >
        <div className="max-w-[85%]">
          <div className="relative rounded-lg rounded-tl-none bg-white px-3 py-2 shadow-sm">
            {/* Bubble tail */}
            <span
              className="absolute -left-2 top-0 h-3 w-3"
              style={{ backgroundColor: '#fff', clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }}
            />

            {mediaHeader && (headerImage ? (
              <img
                src={headerImage}
                alt=""
                className="mb-2 max-h-40 w-full rounded object-cover"
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="mb-2 flex h-24 items-center justify-center rounded bg-gray-100 text-xs font-medium uppercase tracking-wide text-gray-500">
                {headerType} header
              </div>
            ))}
            {headerType === 'text' && headerText && (
              <div className="mb-1 font-semibold text-gray-900">
                <WaText text={headerText} values={placeholderValues} />
              </div>
            )}

            <div className="whitespace-pre-wrap break-words text-[15px] leading-snug text-gray-900">
              {bodyText
                ? <WaText text={bodyText} values={placeholderValues} />
                : <span className="text-gray-400">Your message body will appear here</span>}
            </div>

            {footerText && (
              <div className="mt-1 text-[13px] leading-snug text-gray-500">
                <WaText text={footerText} values={placeholderValues} />
              </div>
            )}

            <div className="mt-1 text-right text-[11px] leading-none text-gray-500">{now}</div>

            {list.length > 0 && (
              <div className="-mx-3 mt-2 border-t border-gray-200">
                {list.map((b, i) => {
                  const Icon = BUTTON_ICON[String(b.kind || '').toUpperCase()] || FiCornerUpLeft;
                  return (
                    <div
                      key={`wa-btn-${i}`}
                      className={`flex items-center justify-center gap-2 px-3 py-2 text-[15px] font-medium text-[#D1272F] ${
                        i > 0 ? 'border-t border-gray-200' : ''
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{b.text || 'Button text'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Templates() {
  // Templates state
  const [templates, setTemplates]               = useState([]);
  const [pendingTemplates, setPendingTemplates] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);

  // Notification categories
  const [categories, setCategories] = useState({});

  // Modal & form state
  const [showModal, setShowModal]               = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [deleteTarget, setDeleteTarget]         = useState(null);

  // View-details modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTemplate, setViewTemplate]   = useState(null);
  const [searchQuery, setSearchQuery]     = useState('');
  const [statusFilter, setStatusFilter]   = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  // Form fields
  const [templateName, setTemplateName]           = useState('');
  const [notifCategory, setNotifCategory]         = useState('');
  const [waCategory, setWaCategory]               = useState('');
  const [language, setLanguage]                   = useState('en_US');
  const [headerType, setHeaderType]               = useState('none');
  const [headerText, setHeaderText]               = useState('');
  const [headerImage, setHeaderImage]             = useState('');
  const [bodyText, setBodyText]                   = useState('');
  const [buttons, setButtons]                     = useState([]);
  const [placeholders, setPlaceholders]           = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [submitting, setSubmitting]               = useState(false);

  // AUTHENTICATION-only settings. Meta owns the copy of these templates, so
  // the operator picks options rather than typing a body.
  const [authSecurityRecommendation, setAuthSecurityRecommendation] = useState(true);
  const [authCodeExpiryMinutes, setAuthCodeExpiryMinutes]           = useState(AUTH_DEFAULT_EXPIRY_MINUTES);

  const isAuthTemplate = String(waCategory || '').toUpperCase() === 'AUTHENTICATION';

  // Which WhatsApp account's templates are on screen. Templates live on a
  // WABA, so this is not a filter over one list — each account has its own.
  const {
    accounts: wabaAccounts,
    selected: wabaAccount,
    setSelected: setWabaAccount,
    loading: wabaLoading,
    error: wabaError,
  } = useWabaAccounts();

  // The account the create dialog will submit to. Seeded from the page's
  // selection when the dialog opens, then independent — changing it must not
  // reload the list behind the dialog.
  const [createAccount, setCreateAccount] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    // Waits for the account list: firing before it resolves would load the
    // default account's templates and then replace them a moment later, which
    // reads as the page flickering between two businesses.
    if (!wabaAccount) return;
    loadTemplates();
  }, [wabaAccount]);

  function loadCategories() {
    fetch('/api/notification-categories')
      .then(res => res.ok ? res.json() : res.text().then(t => { throw new Error(t); }))
      .then(data => data.success && setCategories(data.categories))
      .catch(() => {/* ignore */});
  }

  function loadTemplates() {
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(`/api${withAccount('/templates?status=PENDING', wabaAccount)}`),
      fetch(`/api${withAccount('/templates?status=APPROVED', wabaAccount)}`),
      fetch(`/api${withAccount('/templates?status=REJECTED', wabaAccount)}`),
    ])
      .then(async ([pRes, aRes, rRes]) => {
        if (!pRes.ok || !aRes.ok || !rRes.ok) {
          const texts = await Promise.all([pRes.text(), aRes.text(), rRes.text()]);
          throw new Error(`P:${texts[0]} | A:${texts[1]} | R:${texts[2]}`);
        }
        const [pJson, aJson, rJson] = await Promise.all([pRes.json(), aRes.json(), rRes.json()]);
        return {
          pending:  Array.isArray(pJson.templates)  ? pJson.templates  : (Array.isArray(pJson.data)  ? pJson.data  : []),
          approved: Array.isArray(aJson.templates)  ? aJson.templates  : (Array.isArray(aJson.data)  ? aJson.data  : []),
          rejected: Array.isArray(rJson.templates)  ? rJson.templates  : (Array.isArray(rJson.data)  ? rJson.data  : []),
        };
      })
      .then(({ pending, approved, rejected }) => {
        setPendingTemplates(pending);
        setTemplates([...approved, ...rejected]);
      })
      .catch(err => setError(`Failed to load templates: ${err.message}`))
      .finally(() => setLoading(false));
  }

  function scanPlaceholders() {
    const re = /\{\{(\d+)\}\}/g, found = new Set();
    let m;
    while ((m = re.exec(bodyText)) !== null) found.add(m[1]);
    const phs = Array.from(found).sort((a, b) => +a - +b);
    setPlaceholders(phs);
    setPlaceholderValues(vals => {
      const out = { ...vals };
      phs.forEach(i => { if (!(i in out)) out[i] = ''; });
      return out;
    });
  }

  function resetForm() {
    setTemplateName('');
    setNotifCategory('');
    setWaCategory('');
    setLanguage('en_US');
    setHeaderType('none');
    setHeaderText('');
    setHeaderImage('');
    setBodyText('');
    setButtons([]);
    setPlaceholders([]);
    setPlaceholderValues({});
    setAuthSecurityRecommendation(true);
    setAuthCodeExpiryMinutes(AUTH_DEFAULT_EXPIRY_MINUTES);
  }

  function handleWaCategoryChange(next) {
    setWaCategory(next);
    // An authentication template is nothing without its OTP button, and the
    // header has no place in one; seed the form so it is valid as soon as the
    // category is picked.
    if (String(next).toUpperCase() === 'AUTHENTICATION') {
      setHeaderType('none');
      setHeaderText('');
      setHeaderImage('');
      setButtons(prev => {
        const kinds = prev.map(b => String(b?.kind || '').toUpperCase());
        if (kinds.includes('OTP_COPY_CODE') || kinds.includes('OTP_ONE_TAP')) return prev;
        return [{ ...newTemplateButton(), kind: 'OTP_COPY_CODE', text: 'Copy code' }];
      });
    } else {
      setButtons(prev => prev.filter(b => !String(b?.kind || '').toUpperCase().startsWith('OTP_')));
    }
  }

  function handleEdit(tpl) {
    resetForm();
    setTemplateName(tpl.name);
    setNotifCategory('');
    setWaCategory(tpl.category);
    setLanguage(tpl.language || 'en_US');

    const hdr = tpl.components.find(c => c.type === 'HEADER');
    if (hdr) {
      if (hdr.format === 'TEXT') {
        setHeaderType('text');
        setHeaderText(hdr.text || '');
      } else if (hdr.format === 'IMAGE') {
        setHeaderType('image');
        setHeaderImage(hdr.image?.link || '');
      }
    }

    const bdy = tpl.components.find(c => c.type === 'BODY');
    const isAuth = String(tpl.category || '').toUpperCase() === 'AUTHENTICATION';
    if (isAuth) {
      setBodyText('');
      setAuthSecurityRecommendation(!!bdy?.add_security_recommendation);
      const ftr = tpl.components.find(c => c.type === 'FOOTER');
      const mins = ftr?.code_expiration_minutes;
      setAuthCodeExpiryMinutes(mins ? String(mins) : '');
    } else {
      setBodyText(bdy?.text || '');
    }

    const btnComp = tpl.components.find(c => c.type === 'BUTTONS');
    const btns = Array.isArray(btnComp?.buttons) ? btnComp.buttons.slice(0, MAX_TEMPLATE_BUTTONS).map(normalizeTemplateButton) : [];
    setButtons(btns);

    const re = /\{\{(\d+)\}\}/g, found = new Set();
    let m2;
    while ((m2 = re.exec(bdy?.text || '')) !== null) found.add(m2[1]);
    const phs = Array.from(found).sort((a, b) => +a - +b);
    setPlaceholders(phs);
    const pv = {};
    phs.forEach(idx => { pv[idx] = ''; });
    setPlaceholderValues(pv);

    setShowModal(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!templateName || !notifCategory || !waCategory) return;
    if (!isAuthTemplate && !bodyText) return;

    const buttonError = validateTemplateButtons(buttons, waCategory);
    if (buttonError) {
      alert(buttonError);
      return;
    }

    let expiryMinutes = null;
    if (isAuthTemplate && String(authCodeExpiryMinutes).trim() !== '') {
      expiryMinutes = parseInt(authCodeExpiryMinutes, 10);
      if (!Number.isFinite(expiryMinutes) || expiryMinutes < 1 || expiryMinutes > 90) {
        alert('Code expiry must be between 1 and 90 minutes, or left blank.');
        return;
      }
    }

    setSubmitting(true);

    const comps = [];
    if (isAuthTemplate) {
      // Meta rejects any `text` on these components: the copy is theirs, the
      // form only sends the settings.
      comps.push({ type: 'BODY', add_security_recommendation: authSecurityRecommendation });
      if (expiryMinutes !== null) {
        comps.push({ type: 'FOOTER', code_expiration_minutes: expiryMinutes });
      }
    } else {
      const re = /\{\{(\d+)\}\}/g;
      const phsSet = new Set();
      let match;
      while ((match = re.exec(bodyText)) !== null) {
        phsSet.add(match[1]);
      }
      const phs = Array.from(phsSet).sort((a, b) => +a - +b);

      if (headerType === 'text' && headerText) {
        comps.push({ type: 'HEADER', format: 'TEXT', text: headerText });
      } else if (headerType === 'image' && headerImage) {
        comps.push({ type: 'HEADER', format: 'IMAGE', image: { link: headerImage } });
      }

      const bodyComp = { type: 'BODY', text: bodyText };
      if (phs.length > 0) {
        bodyComp.example = { body_text: [ phs.map(idx => placeholderValues[idx] || '') ] };
      }
      comps.push(bodyComp);
    }

    if (buttons.length > 0) {
      comps.push({
        type: 'BUTTONS',
        buttons: buttons.map(buildTemplateButtonPayload)
      });
    }

    const payload = {
      name: templateName,
      language,
      category: waCategory,
      components: comps,
      // The API also accepts this as a query parameter; sending it in the body
      // keeps the create form to a single JSON post.
      waba_account: createAccount || wabaAccount,
    };

    fetch(`/api${withAccount('/templates', createAccount || wabaAccount)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res =>
        res.ok
          ? res.json()
          : res.text().then(t => { throw new Error(t); })
      )
      .then(data => {
        setSubmitting(false);
        if (data.success) {
          setShowModal(false);
          resetForm();
          // Follow the template to the account it was created on, so the new
          // PENDING row is actually in the list the operator is looking at.
          if (createAccount && createAccount !== wabaAccount) {
            setWabaAccount(createAccount);   // triggers the reload itself
          } else {
            loadTemplates();
          }
          setShowSuccessModal(true);
        } else {
          alert(data.details || data.error || 'Submission failed');
        }
      })
      .catch(err => {
        setSubmitting(false);
        alert('Network/API error: ' + err.message);
      });
  }

  function onDeleteClick(name) {
    setDeleteTarget(name);
    setShowDeleteModal(true);
  }

  function confirmDelete() {
    fetch(`/api${withAccount(`/templates/${encodeURIComponent(deleteTarget)}`, wabaAccount)}`,
          { method: 'DELETE' })
      .then(res =>
        res.ok
          ? res.json()
          : res.text().then(t => { throw new Error(t); })
      )
      .then(() => {
        setShowDeleteModal(false);
        loadTemplates();
      })
      .catch(err => {
        setShowDeleteModal(false);
        alert('Delete failed: ' + err.message);
      });
  }

  function handleView(tpl) {
    setViewTemplate(tpl);
    setShowViewModal(true);
  }

  function addButton() {
    setButtons(prev => {
      if (isAuthTemplate) {
        if (prev.length >= 1) return prev;
        return [{ ...newTemplateButton(), kind: 'OTP_COPY_CODE', text: 'Copy code' }];
      }
      if (prev.length >= MAX_TEMPLATE_BUTTONS) return prev;
      return [...prev, newTemplateButton()];
    });
  }

  function removeButton(index) {
    setButtons(prev => prev.filter((_, i) => i !== index));
  }

  function updateButton(index, patch) {
    setButtons(prev =>
      prev.map((btn, i) => (i === index ? { ...btn, ...patch } : btn))
    );
  }

  const displayed = [...pendingTemplates, ...templates];
  const query = searchQuery.trim().toLowerCase();
  const filteredTemplates = displayed.filter((tpl) => {
    const templateStatus = String(tpl?.status || '').toUpperCase();
    const matchesStatus = statusFilter === 'ALL' || templateStatus === statusFilter;
    if (!matchesStatus) return false;

    const templateCategory = String(tpl?.category || '').toUpperCase();
    if (categoryFilter !== 'ALL' && templateCategory !== categoryFilter) return false;

    if (!query) return true;
    const bodyText = tpl?.components?.find(c => String(c?.type || '').toUpperCase() === 'BODY')?.text || '';
    const haystack = [
      tpl?.name || '',
      tpl?.id || '',
      tpl?.category || '',
      tpl?.language || '',
      templateStatus,
      bodyText
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  });

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto" style={{ maxWidth: '1800px' }}>

          {/* Header + Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">WhatsApp Template Management</h1>
              <p className="text-gray-600 mt-1">
                Manage message templates
                {wabaAccount && (
                  <>
                    {' for '}
                    <span className="font-semibold text-gray-800">
                      {accountLabel(wabaAccounts.find(a => a.name === wabaAccount)) || wabaAccount}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadTemplates}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <FiRefreshCw className="w-5 h-5" /> Refresh
              </button>
              <button
                onClick={() => { resetForm(); setCreateAccount(wabaAccount); setShowModal(true); }}
                className="flex items-center gap-2 px-4 py-2 text-white bg-[#F96168] rounded-lg hover:bg-[#EA434B]"
              >
                <FiPlus className="w-5 h-5" /> Create Template
              </button>
            </div>
          </div>

          {/* Search + Status Filter */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
            {/* Account first, because it decides WHICH templates the filters
                below then narrow down. */}
            <div className="mb-3 flex flex-col gap-2 border-b border-gray-100 pb-3 sm:flex-row sm:items-center">
              <label htmlFor="tpl-waba" className="text-sm font-medium text-gray-700 sm:w-44">
                WhatsApp Account
              </label>
              <select
                id="tpl-waba"
                value={wabaAccount}
                disabled={wabaLoading || !wabaAccounts.length}
                onChange={(e) => setWabaAccount(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 sm:max-w-sm"
              >
                {wabaLoading && <option value="">Loading accounts…</option>}
                {!wabaLoading && !wabaAccounts.length && (
                  <option value="">No accounts configured</option>
                )}
                {!wabaLoading && wabaAccounts.map((a) => (
                  <option key={a.name} value={a.name}>{accountLabel(a)}</option>
                ))}
              </select>
              {wabaError && <p className="text-xs text-red-600">{wabaError}</p>}
              {!wabaError && !wabaLoading && !wabaAccounts.length && (
                <p className="text-xs text-amber-700">
                  Add one under Settings → WhatsApp Business Accounts.
                </p>
              )}
            </div>
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, status, category, language, or body text..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div className="w-full lg:w-56">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="PENDING">PENDING</option>
                  <option value="REJECTED">REJECTED</option>
                </select>
              </div>
              <div className="w-full lg:w-56">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="ALL">All Categories</option>
                  {WA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Showing {filteredTemplates.length} of {displayed.length} templates
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-4 border-brand-500 border-gray-200"></div>
              <p className="mt-4 text-gray-600">Loading templates…</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-red-700">
                <FiAlertTriangle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && displayed.length === 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-yellow-700">
                <FiAlertCircle className="w-5 h-5" />
                <span>No templates found. Create one to get started.</span>
              </div>
            </div>
          )}

          {!loading && !error && displayed.length > 0 && filteredTemplates.length === 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-yellow-700">
                <FiAlertCircle className="w-5 h-5" />
                <span>No templates match the current search/filter.</span>
              </div>
            </div>
          )}

          {/* Cards Grid */}
          {!loading && !error && filteredTemplates.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map(t => (
                <div
                  key={t.id}
                  /* min-w-0: a grid item defaults to min-width:auto, so a long
                     unbroken template name would stretch the whole tile. */
                  className="min-w-0 bg-white border border-gray-200 rounded-xl p-6 flex flex-col justify-between hover:shadow-lg transition-shadow"
                >
                  <div className="min-w-0">
                    <div className="flex justify-between items-start gap-3 mb-4">
                      {/* Names are underscore-joined timestamps with no natural
                          break opportunity, and the tail is what distinguishes
                          them — so wrap over two lines rather than truncating
                          the distinguishing part away. */}
                      <h3
                        className="min-w-0 flex-1 break-words line-clamp-2 text-lg font-semibold text-gray-900"
                        title={t.name}
                      >
                        {t.name}
                      </h3>
                      <span
                        className={`shrink-0 whitespace-nowrap px-2 py-0.5 text-sm rounded-full ${
                          t.status === 'APPROVED'
                            ? 'bg-green-100 text-green-800'
                            : t.status === 'REJECTED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {t.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs mb-4">
                      <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded">{t.category}</span>
                      <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                        {t.language?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-3 mb-6">
                      {t.components?.find(c => c.type.toLowerCase() === 'body')?.text || 'No preview available'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleView(t)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded text-gray-700"
                    >
                      <FiEye className="w-4 h-4" /> View
                    </button>
                    <button
                      onClick={() => onDeleteClick(t.name)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 rounded text-red-800"
                    >
                      <FiTrash className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}


          {showModal && (
            <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
              <div className="bg-gray-800 text-gray-100 rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

                {/* Modal Header */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <FiFileText className="w-6 h-6 text-white" />
                    {templateName ? 'Edit WhatsApp Template' : 'Create New WhatsApp Template'}
                  </h3>
                  <button onClick={() => setShowModal(false)}>
                    <FiX className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
                  </button>
                </div>

                {/* Modal Body */}
                <form
                  id="templateForm"
                  onSubmit={handleSubmit}
                  className="p-6 space-y-6 overflow-y-auto flex-1"
                >
                  {/* Which WhatsApp Business Account this template is created
                      on. First field in the form because it decides which
                      business the template belongs to, and it cannot be
                      changed after Meta approves it. */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">WhatsApp Account</label>
                    <select
                      value={createAccount}
                      disabled={wabaLoading || !wabaAccounts.length}
                      onChange={e => setCreateAccount(e.target.value)}
                      className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500 disabled:opacity-60"
                      required
                    >
                      {wabaLoading && <option value="">Loading accounts…</option>}
                      {!wabaLoading && !wabaAccounts.length && (
                        <option value="">No accounts configured</option>
                      )}
                      {!wabaLoading && wabaAccounts.map(a => (
                        <option key={a.name} value={a.name}>{accountLabel(a)}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">
                      The template is submitted to this account's WhatsApp Business
                      Account and can only be sent from it.
                    </p>
                  </div>

                  {/* Notification Category */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Notification Category</label>
                    <select
                      value={notifCategory}
                      onChange={e => setNotifCategory(e.target.value)}
                      className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                      required
                    >
                      <option value="" disabled>Select a notification type</option>
                      {Object.entries(categories).map(([key]) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">
                      Select a notification category to automatically populate template details.
                    </p>
                  </div>

                  {/* Template Name */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Template Name</label>
                    <input
                      type="text"
                      value={templateName}
                      onChange={e => setTemplateName(e.target.value)}
                      placeholder="Enter template name"
                      className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                      required
                    />
                  </div>

                  {/* WhatsApp Category & Language */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium">WhatsApp Category</label>
                      <select
                        value={waCategory}
                        onChange={e => handleWaCategoryChange(e.target.value)}
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                        required
                      >
                        <option value="" disabled>Select a category</option>
                        {WA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium">Language</label>
                      <select
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                      >
                        <option value="en_US">English (US)</option>
                        <option value="en_GB">English (UK)</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="es">Spanish</option>
                        <option value="pt">Portuguese</option>
                        <option value="it">Italian</option>
                        <option value="ru">Russian</option>
                        <option value="zh_CN">Chinese (Simplified)</option>
                        <option value="zh_TW">Chinese (Traditional)</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="id">Indonesian</option>
                        <option value="ms">Malay</option>
                        <option value="th">Thai</option>
                        <option value="vi">Vietnamese</option>
                        <option value="tr">Turkish</option>
                        <option value="ar">Arabic</option>
                        <option value="hi">Hindi</option>
                        <option value="bn">Bengali</option>
                        <option value="gu">Gujarati</option>
                        <option value="kn">Kannada</option>
                        <option value="ml">Malayalam</option>
                        <option value="mr">Marathi</option>
                        <option value="pa">Punjabi</option>
                        <option value="ta">Tamil</option>
                        <option value="te">Telugu</option>
                        <option value="ur">Urdu</option>
                      </select>
                    </div>
                  </div>

                  {/* Authentication templates: Meta writes the message, we only pick settings */}
                  {isAuthTemplate && (
                    <AccordionSection title="Authentication Settings" defaultOpen={true}>
                      <div className="space-y-4">
                        <p className="text-xs text-gray-400">
                          WhatsApp writes the text of authentication templates itself, so there is
                          no header or body to type. Choose the options below; the preview shows the
                          message your customers will receive.
                        </p>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={authSecurityRecommendation}
                            onChange={e => setAuthSecurityRecommendation(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-gray-500 bg-gray-700 text-brand-500 focus:ring-brand-500"
                          />
                          <span>
                            <span className="block text-sm text-gray-200">Add security recommendation</span>
                            <span className="block text-xs text-gray-400">
                              Appends "For your security, do not share this code." to the message.
                            </span>
                          </span>
                        </label>
                        <div className="space-y-1">
                          <label className="block text-sm">Code expires in (minutes)</label>
                          <input
                            type="number"
                            min={1}
                            max={90}
                            value={authCodeExpiryMinutes}
                            onChange={e => setAuthCodeExpiryMinutes(e.target.value)}
                            placeholder="Leave blank for no expiry footer"
                            className="w-full md:w-60 bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                          />
                          <p className="text-xs text-gray-400">
                            1–90. Adds a footer such as "This code expires in 10 minutes." Leave blank to omit it.
                          </p>
                        </div>
                      </div>
                    </AccordionSection>
                  )}

                  {/* Header (Optional) */}
                  {!isAuthTemplate && (
                  <AccordionSection title="Header (Optional)" defaultOpen={false}>
                    <div className="space-y-2">
                      <label className="block text-sm">Header Type</label>
                      <select
                        value={headerType}
                        onChange={e => setHeaderType(e.target.value)}
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                      >
                        <option value="none">No Header</option>
                        <option value="text">Text Header</option>
                        <option value="image">Image Header</option>
                      </select>
                      {headerType === 'text' && (
                        <input
                          type="text"
                          value={headerText}
                          onChange={e => setHeaderText(e.target.value)}
                          placeholder="Header text..."
                          className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                        />
                      )}
                      {headerType === 'image' && (
                        <input
                          type="url"
                          value={headerImage}
                          onChange={e => setHeaderImage(e.target.value)}
                          placeholder="Image URL..."
                          className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                        />
                      )}
                    </div>
                  </AccordionSection>
                  )}

                  {/* Body (Required) */}
                  {!isAuthTemplate && (
                  <AccordionSection title="Body (Required)" defaultOpen={true}>
                    <div className="space-y-2">
                      <label className="block text-sm">Body Text</label>
                      <textarea
                        value={bodyText}
                        onChange={e => setBodyText(e.target.value)}
                        rows={4}
                        placeholder="e.g., Hi {{1}}, your order ({{2}}) has been confirmed!"
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-brand-500"
                        required
                      />
                      <p className="text-xs text-gray-400">Include placeholders as needed.</p>
                      <button
                        type="button"
                        onClick={scanPlaceholders}
                        className="flex items-center gap-2 text-brand-700 hover:text-brand-800"
                      >
                        <FiSearch className="w-5 h-5" /> Scan Placeholders
                      </button>
                      {placeholders.length > 0 && (
                        <div className="bg-gray-700 p-4 rounded-lg space-y-2">
                          {placeholders.map(idx => (
                            <div key={idx} className="space-y-1">
                              <label className="text-xs">
                                Value for {'{{'}{idx}{'}}'}
                              </label>
                              <input
                                type="text"
                                value={placeholderValues[idx]}
                                onChange={e =>
                                  setPlaceholderValues(v => ({
                                    ...v,
                                    [idx]: e.target.value
                                  }))
                                }
                                className="w-full bg-gray-600 text-gray-200 px-3 py-1 rounded-lg border border-gray-500 focus:ring-2 focus:ring-brand-500"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionSection>
                  )}

                  {/* Buttons (Optional) */}
                  <AccordionSection title={isAuthTemplate ? 'OTP Button (Required)' : 'Buttons (Optional)'} defaultOpen={isAuthTemplate}>
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs text-gray-400">
                          {isAuthTemplate
                            ? 'Authentication templates carry exactly one OTP button (Copy Code or One Tap).'
                            : `Add up to ${MAX_TEMPLATE_BUTTONS} buttons.`}
                        </p>
                        <button
                          type="button"
                          onClick={addButton}
                          disabled={buttons.length >= (isAuthTemplate ? 1 : MAX_TEMPLATE_BUTTONS)}
                          className="px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-500 text-ink text-sm disabled:opacity-60"
                        >
                          Add Button
                        </button>
                      </div>

                      {buttons.length === 0 && (
                        <div className="text-xs text-gray-400">
                          No buttons added. You can create quick reply, URL, phone, OTP, catalog, or MPM buttons.
                        </div>
                      )}

                      {buttons.map((btn, idx) => (
                        <div key={`btn-${idx}`} className="rounded-lg border border-gray-600 bg-gray-700/70 p-3 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-medium text-gray-100">Button {idx + 1}</div>
                            <button
                              type="button"
                              onClick={() => removeButton(idx)}
                              className="px-2.5 py-1 rounded bg-red-600/80 hover:bg-red-600 text-white text-xs"
                            >
                              Remove
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="block text-xs text-gray-300">Button Type</label>
                              <select
                                value={btn.kind}
                                onChange={e => updateButton(idx, { kind: e.target.value })}
                                className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                              >
                                {TEMPLATE_BUTTON_TYPES
                                  .filter(opt => isAuthTemplate
                                    ? opt.value.startsWith('OTP_')
                                    : !opt.value.startsWith('OTP_'))
                                  .map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="block text-xs text-gray-300">Button Text</label>
                              <input
                                type="text"
                                value={btn.text}
                                onChange={e => updateButton(idx, { text: e.target.value })}
                                placeholder="Visible button label"
                                className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                              />
                            </div>
                          </div>

                          {btn.kind === 'URL' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <label className="block text-xs text-gray-300">URL</label>
                                <input
                                  type="url"
                                  value={btn.url}
                                  onChange={e => updateButton(idx, { url: e.target.value })}
                                  placeholder="https://example.com/offer?promo={{1}}"
                                  className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="block text-xs text-gray-300">URL Example (Optional)</label>
                                <input
                                  type="text"
                                  value={btn.urlExample}
                                  onChange={e => updateButton(idx, { urlExample: e.target.value })}
                                  placeholder="summer2026"
                                  className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                                />
                              </div>
                            </div>
                          )}

                          {btn.kind === 'PHONE_NUMBER' && (
                            <div className="space-y-1">
                              <label className="block text-xs text-gray-300">Phone Number</label>
                              <input
                                type="text"
                                value={btn.phone_number}
                                onChange={e => updateButton(idx, { phone_number: e.target.value })}
                                placeholder="15550001234"
                                className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                              />
                            </div>
                          )}

                          {btn.kind === 'OTP_ONE_TAP' && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <label className="block text-xs text-gray-300">Autofill Text</label>
                                <input
                                  type="text"
                                  value={btn.autofill_text}
                                  onChange={e => updateButton(idx, { autofill_text: e.target.value })}
                                  placeholder="Autofill"
                                  className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="block text-xs text-gray-300">Package Name</label>
                                <input
                                  type="text"
                                  value={btn.package_name}
                                  onChange={e => updateButton(idx, { package_name: e.target.value })}
                                  placeholder="com.example.app"
                                  className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="block text-xs text-gray-300">Signature Hash</label>
                                <input
                                  type="text"
                                  value={btn.signature_hash}
                                  onChange={e => updateButton(idx, { signature_hash: e.target.value })}
                                  placeholder="K8a%2FAINcGX7"
                                  className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded-lg border border-gray-600"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionSection>

                  {/* Preview */}
                  <div className="mt-4">
                    <div className="mb-2 text-sm font-semibold text-gray-200">Template Preview</div>
                    {isAuthTemplate ? (
                      <WhatsAppPreview
                        headerType="none"
                        bodyText={authBodyText(authSecurityRecommendation)}
                        footerText={authFooterText(authCodeExpiryMinutes)}
                        buttons={buttons}
                        placeholderValues={{ '1': '123456' }}
                      />
                    ) : (
                      <WhatsAppPreview
                        headerType={headerType}
                        headerText={headerText}
                        headerImage={headerImage}
                        bodyText={bodyText}
                        buttons={buttons}
                        placeholderValues={placeholderValues}
                      />
                    )}
                    <p className="mt-2 text-xs text-gray-400">
                      {isAuthTemplate
                        ? 'This is the text WhatsApp generates for the options above (shown in English; it is translated for the chosen language). The code itself is filled in when you send.'
                        : 'Sample values from the Placeholders section are substituted in; anything still highlighted has no sample and will reach Meta as a variable.'}
                    </p>
                  </div>
                </form>

                {/* Modal Actions */}
                <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t border-gray-700 px-6 py-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="templateForm"
                    disabled={submitting}
                    className="px-4 py-2 rounded-lg flex items-center gap-2"
                    style={{ backgroundColor: '#F96168' }}
                  >
                    {submitting ? (
                      <>
                        <FiLoader className="animate-spin w-5 h-5" /> Submitting…
                      </>
                    ) : (
                      <>
                        <FiSave className="w-5 h-5" /> Submit Template
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
              <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between gap-3">
                  <h3 className="text-lg	font-semibold text-gray-900">Confirm Deletion</h3>
                  <button onClick={() => setShowDeleteModal(false)}>
                    <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-gray-800">
                    Are you sure you want to delete template "<span className="font-medium">{deleteTarget}</span>"?
                  </p>
                </div>
                <div className="flex justify-end gap-3 px-6 py-4 border-t">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4	py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4	py-2 bg-red-600 rounded-lg hover:bg-red-700 text-white"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Modal */}
          {showSuccessModal && (
            <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
              <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Success
                  </h3>
                  <button onClick={() => setShowSuccessModal(false)}>
                    <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-gray-800">
                    Your template has been submitted for approval.
                  </p>
                </div>
                <div className="flex justify-end gap-3 px-6 py-4 border-t">
                  <button
                    onClick={() => setShowSuccessModal(false)}
                    className="px-4 py-2 bg-brand-500 rounded-lg hover:bg-brand-600 text-ink"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* View Details Modal */}
          {showViewModal && viewTemplate && (
            <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 p-0 sm:items-center sm:p-4">
              <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">Template Details</h3>
                  <button onClick={() => setShowViewModal(false)}>
                    <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
                  </button>
                </div>
                <div className="p-6 space-y-6 overflow-y-auto">
                  {/* First, because "what does this actually look like" is the
                      question the raw component dump below cannot answer. */}
                  <div className="max-w-md">
                    <WhatsAppPreview {...previewPropsFromTemplate(viewTemplate)} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(viewTemplate || {})
                      .filter(([k]) => k !== 'components')
                      .map(([k, v]) => (
                        <div key={`meta-${k}`} className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                          <div className="text-xs text-gray-500 mb-1">{formatFieldLabel(k)}</div>
                          <div className="text-sm text-gray-900">
                            <DetailValue value={v} />
                          </div>
                        </div>
                      ))}
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Components</h4>
                    {Array.isArray(viewTemplate.components) && viewTemplate.components.length > 0 ? (
                      <div className="space-y-4">
                        {viewTemplate.components.map((comp, i) => {
                          const type = String(comp?.type || '').toUpperCase();
                          const knownKeys = new Set(['type', 'format', 'text', 'example', 'parameters', 'buttons', 'image', 'video', 'document']);
                          const extraEntries = Object.entries(comp || {}).filter(([k]) => !knownKeys.has(k));

                          return (
                            <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="px-2 py-0.5 rounded-full bg-brand-100 text-brand-800 text-xs font-medium">
                                  Component {i + 1}
                                </span>
                                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">
                                  {type || '-'}
                                </span>
                                {comp?.format ? (
                                  <span className="px-2 py-0.5 rounded-full bg-brand-100 text-brand-800 text-xs font-medium">
                                    {String(comp.format).toUpperCase()}
                                  </span>
                                ) : null}
                              </div>

                              {comp?.text ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Text</div>
                                  <div className="text-sm text-gray-900 whitespace-pre-wrap">{comp.text}</div>
                                </div>
                              ) : null}

                              {comp?.image?.link ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Image URL</div>
                                  <DetailValue value={comp.image.link} />
                                </div>
                              ) : null}

                              {comp?.video?.link ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Video URL</div>
                                  <DetailValue value={comp.video.link} />
                                </div>
                              ) : null}

                              {comp?.document?.link ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Document URL</div>
                                  <DetailValue value={comp.document.link} />
                                </div>
                              ) : null}

                              {Array.isArray(comp?.buttons) && comp.buttons.length > 0 ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-2">Buttons ({comp.buttons.length})</div>
                                  <div className="space-y-2">
                                    {comp.buttons.map((btn, bIdx) => (
                                      <div key={`comp-${i}-btn-${bIdx}`} className="rounded-md border border-gray-200 p-3 bg-white">
                                        <div className="text-xs font-medium text-gray-700 mb-2">Button {bIdx + 1}</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {Object.entries(btn || {}).map(([bk, bv]) => (
                                            <div key={`btn-${bIdx}-${bk}`} className="text-sm">
                                              <div className="text-xs text-gray-500">{formatFieldLabel(bk)}</div>
                                              <div className="text-gray-900">
                                                <DetailValue value={bv} />
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              {Array.isArray(comp?.parameters) && comp.parameters.length > 0 ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Parameters</div>
                                  <DetailValue value={comp.parameters} />
                                </div>
                              ) : null}

                              {comp?.example ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Example</div>
                                  <DetailValue value={comp.example} />
                                </div>
                              ) : null}

                              {extraEntries.length > 0 ? (
                                <div>
                                  <div className="text-xs text-gray-500 mb-2">Additional Fields</div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {extraEntries.map(([ek, ev]) => (
                                      <div key={`extra-${i}-${ek}`} className="text-sm">
                                        <div className="text-xs text-gray-500">{formatFieldLabel(ek)}</div>
                                        <div className="text-gray-900">
                                          <DetailValue value={ev} />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500">
                        No components available for this template.
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Raw Template JSON</h4>
                    <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs overflow-auto whitespace-pre-wrap">
                      {JSON.stringify(viewTemplate, null, 2)}
                    </pre>
                  </div>
                </div>
                <div className="flex justify-end px-6 py-4 border-t">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 bg-brand-500 text-ink rounded-lg hover:bg-brand-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
